import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

// Cached CanvasTexture per name so we don't rebuild on every render.
const labelCache = new Map();
function getNameTexture(name) {
  if (!name) return null;
  const safe = String(name).slice(0, 18);
  if (labelCache.has(safe)) return labelCache.get(safe);
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 72;
  const ctx = canvas.getContext("2d");
  // Soft dark pill background
  ctx.fillStyle = "rgba(12, 4, 32, 0.78)";
  const r = 28;
  ctx.beginPath();
  ctx.moveTo(r, 12);
  ctx.lineTo(256 - r, 12);
  ctx.arcTo(256, 12, 256, 12 + r, r);
  ctx.lineTo(256, 60 - r);
  ctx.arcTo(256, 60, 256 - r, 60, r);
  ctx.lineTo(r, 60);
  ctx.arcTo(0, 60, 0, 60 - r, r);
  ctx.lineTo(0, 12 + r);
  ctx.arcTo(0, 12, r, 12, r);
  ctx.fill();
  // Name text
  ctx.font = "bold 30px 'Space Mono', 'Courier New', monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ffe17a";
  ctx.fillText(safe, 128, 36);
  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 4;
  labelCache.set(safe, tex);
  return tex;
}

// A translucent "ghost" UFO that replays a recorded path on loop.
// • `path` is an array of {t, x, z} samples in seconds.
// • Playback loops with a 1.5-second gap between loops.
// • If `name` is provided, a small floating name label is shown overhead.
export default function GhostUFO({ path, name, offset = 0, color = "#9ad8ff" }) {
  const groupRef = useRef();

  const totalDur = useMemo(() => {
    if (!path || path.length < 2) return 0;
    return Math.max(0.1, (path[path.length - 1].t || 0) - (path[0].t || 0));
  }, [path]);
  const loopDur = totalDur + 1.5;

  const labelTexture = useMemo(() => getNameTexture(name), [name]);

  useFrame(({ clock }) => {
    if (!groupRef.current || !path || path.length < 2) return;
    const local = (clock.elapsedTime + offset) % loopDur;
    if (local > totalDur) {
      groupRef.current.visible = false;
      return;
    }
    groupRef.current.visible = true;
    const target = local + (path[0].t || 0);
    let i = 1;
    while (i < path.length && path[i].t < target) i++;
    const a = path[Math.max(0, i - 1)];
    const b = path[Math.min(path.length - 1, i)];
    const span = b.t - a.t;
    const k = span > 0.001 ? (target - a.t) / span : 0;
    const x = a.x + (b.x - a.x) * k;
    const z = a.z + (b.z - a.z) * k;
    const y = 4.2 + Math.sin(clock.elapsedTime * 1.6 + offset) * 0.18;
    groupRef.current.position.set(x, y, z);
    groupRef.current.rotation.y = clock.elapsedTime * 0.5 + offset;
  });

  if (!path || path.length < 2) return null;

  return (
    <group ref={groupRef}>
      <mesh>
        <cylinderGeometry args={[1.6, 1.9, 0.35, 18]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={0.6}
          transparent
          opacity={0.45}
          depthWrite={false}
          metalness={0.4}
          roughness={0.4}
          toneMapped={false}
        />
      </mesh>
      <mesh position={[0, 0.4, 0]}>
        <sphereGeometry args={[0.9, 14, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color="#bafff0"
          emissive="#7ce8c8"
          emissiveIntensity={0.5}
          transparent
          opacity={0.4}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
      <mesh position={[0, -0.25, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[1.4, 2.4, 24]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.18}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
      {labelTexture && (
        <sprite position={[0, 2.0, 0]} scale={[2.6, 0.73, 1]}>
          <spriteMaterial
            attach="material"
            map={labelTexture}
            transparent
            depthWrite={false}
            depthTest={false}
            toneMapped={false}
          />
        </sprite>
      )}
    </group>
  );
}
